import { useCallback } from 'react';
import {
  Box,
  Select,
  Checkbox,
  Button,
  Text,
  HStack,
  VStack,
  Flex,
  Wrap,
  WrapItem,
  Divider,
  useDisclosure,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { WarningIcon, CheckIcon } from '@chakra-ui/icons';

import MultiSelect from 'components/fields/MultiSelect';
import SectionCard from '../../components/SectionCard';
import AccidentBenefitsModal from '../../components/AccidentBenefitsModal';
import AllCoveragesModal from '../../components/AllCoveragesModal';
import LiabilityLimitConfirmationModal from '../../components/LiabilityLimitConfirmationModal';
import DcpdDeclineConfirmationModal from '../../components/DcpdDeclineConfirmationModal';
import PhysicalDamageDeclineConfirmationModal from '../../components/PhysicalDamageDeclineConfirmationModal';
import CoverageConsentModal from '../../components/CoverageConsentModal';
import {
  OPCF_20_LIMIT_PER_DAY_CAD,
  OPCF_27_LIMIT_CAD,
  OPCF_43_INDEMNITY_PERIOD_MONTHS,
  RIDE_SHARING_COMPANIES,
} from '../../constants/vehicleOptions';
import { applyRideSharingDefaults } from '../../utils/coverageDefaults';
import {
  CONSENT_TYPE_OPCF_20_DECLINE,
  CONSENT_TYPE_OPCF_27_DECLINE,
  CONSENT_TYPE_ACCIDENT_WAIVER_DECLINE,
  CONSENT_TYPE_MINOR_CONVICTION_DECLINE,
  CONSENT_TYPE_OPCF_43_DECLINE,
} from '../../constants';
import {
  useAnswerCodes,
  useValidationSchema,
  useLiabilityLimitConfirmation,
  useDcpdDeclineConfirmation,
  usePhysicalDamageDeclineConfirmation,
  useCoverageDeclineConfirmation,
} from '../../hooks';
import type {
  AccidentBenefits,
  VehicleCoverages as VehicleCoveragesType,
} from 'api/quote-marketplace';
import type { VehicleCoveragesProps } from './types';

const RIDE_SHARING_ITEMS = RIDE_SHARING_COMPANIES.map((company, index) => ({
  id: index,
  name: company.label,
  value: company.value,
}));

const rideSharingValueToId = (value: string): number => {
  const item = RIDE_SHARING_ITEMS.find((i) => i.value === value);
  return item?.id ?? -1;
};

const rideSharingIdToValue = (id: number): string => {
  const item = RIDE_SHARING_ITEMS.find((i) => i.id === id);
  return item?.value ?? '';
};

const VehicleCoverages = ({
  vehicle,
  index,
  onVehicleChange,
  quoteId,
  provinceCode,
}: VehicleCoveragesProps) => {
  const {
    getOptions,
    getLabel,
    isLoading: isLoadingCodes,
    isOtherDeductible,
  } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const handleApplyLiabilityChange = useCallback(
    (newCode: string, clearOther: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        liability_limit_cad: newCode,
        liability_limit_other_cad: clearOther
          ? null
          : vehicle.coverages?.liability_limit_other_cad,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const handleApplyDcpdChange = useCallback(
    (newCode: string, clearOther: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        dcpd_deductible_cad: newCode,
        dcpd_deductible_other_cad: clearOther
          ? null
          : vehicle.coverages?.dcpd_deductible_other_cad,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const { handleLiabilityLimitChange, modalProps: liabilityModalProps } =
    useLiabilityLimitConfirmation({
      quoteId,
      vehicleVin: vehicle.vin || '',
      vehicleId: vehicle.id,
      currentLiabilityCode: vehicle.coverages?.liability_limit_cad,
      getLabel,
      isOtherDeductible,
      onApplyChange: handleApplyLiabilityChange,
    });

  const { handleDcpdChange, modalProps: dcpdModalProps } =
    useDcpdDeclineConfirmation({
      quoteId,
      vehicleVin: vehicle.vin || '',
      vehicleId: vehicle.id,
      currentDcpdCode: vehicle.coverages?.dcpd_deductible_cad,
      isOtherDeductible,
      onApplyChange: handleApplyDcpdChange,
    });

  const clearAllPhysicalDamage = useCallback(() => {
    onVehicleChange(index, 'coverages', {
      ...vehicle.coverages,
      collision_enabled: false,
      comprehensive_enabled: false,
      all_perils_enabled: false,
      collision_deductible_cad: '',
      collision_deductible_other_cad: null,
      comprehensive_deductible_cad: '',
      comprehensive_deductible_other_cad: null,
      all_perils_deductible_cad: '',
      all_perils_deductible_other_cad: null,
    });
  }, [index, onVehicleChange, vehicle.coverages]);

  const {
    triggerConfirmation: triggerPhysicalDamageDecline,
    modalProps: physicalDamageModalProps,
  } = usePhysicalDamageDeclineConfirmation({
    quoteId,
    vehicleVin: vehicle.vin || '',
    vehicleId: vehicle.id,
    onConfirmDecline: clearAllPhysicalDamage,
  });

  const handleApplyOpcf20Change = useCallback(
    (enabled: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        opcf_20_enabled: enabled,
        opcf_20_limit_per_day_cad: enabled
          ? vehicle.coverages?.opcf_20_limit_per_day_cad
          : null,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const { handleChange: handleOpcf20Change, modalProps: opcf20ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin: vehicle.vin || '',
      vehicleId: vehicle.id,
      consentType: CONSENT_TYPE_OPCF_20_DECLINE,
      currentEnabled: vehicle.coverages?.opcf_20_enabled || false,
      onApplyChange: handleApplyOpcf20Change,
    });

  const handleApplyOpcf27Change = useCallback(
    (enabled: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        opcf_27_enabled: enabled,
        opcf_27_limit_cad: enabled
          ? vehicle.coverages?.opcf_27_limit_cad
          : null,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const { handleChange: handleOpcf27Change, modalProps: opcf27ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin: vehicle.vin || '',
      vehicleId: vehicle.id,
      consentType: CONSENT_TYPE_OPCF_27_DECLINE,
      currentEnabled: vehicle.coverages?.opcf_27_enabled || false,
      onApplyChange: handleApplyOpcf27Change,
    });

  const handleApplyAccidentWaiverChange = useCallback(
    (enabled: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        accident_waiver: enabled,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const {
    handleChange: handleAccidentWaiverChange,
    modalProps: accidentWaiverModalProps,
  } = useCoverageDeclineConfirmation({
    quoteId,
    vehicleVin: vehicle.vin || '',
    vehicleId: vehicle.id,
    consentType: CONSENT_TYPE_ACCIDENT_WAIVER_DECLINE,
    currentEnabled: vehicle.coverages?.accident_waiver || false,
    onApplyChange: handleApplyAccidentWaiverChange,
  });

  const handleApplyMinorConvictionChange = useCallback(
    (enabled: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        minor_conviction_protection: enabled,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const {
    handleChange: handleMinorConvictionChange,
    modalProps: minorConvictionModalProps,
  } = useCoverageDeclineConfirmation({
    quoteId,
    vehicleVin: vehicle.vin || '',
    vehicleId: vehicle.id,
    consentType: CONSENT_TYPE_MINOR_CONVICTION_DECLINE,
    currentEnabled: vehicle.coverages?.minor_conviction_protection || false,
    onApplyChange: handleApplyMinorConvictionChange,
  });

  const handleApplyOpcf43Change = useCallback(
    (enabled: boolean) => {
      onVehicleChange(index, 'coverages', {
        ...vehicle.coverages,
        opcf_43_enabled: enabled,
        opcf_43_indemnity_period_months: enabled
          ? vehicle.coverages?.opcf_43_indemnity_period_months
          : null,
      });
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const { handleChange: handleOpcf43Change, modalProps: opcf43ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin: vehicle.vin || '',
      vehicleId: vehicle.id,
      consentType: CONSENT_TYPE_OPCF_43_DECLINE,
      currentEnabled: vehicle.coverages?.opcf_43_enabled || false,
      onApplyChange: handleApplyOpcf43Change,
    });

  const handleCollisionToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        onVehicleChange(index, 'coverages', {
          ...vehicle.coverages,
          collision_enabled: true,
          all_perils_enabled: false,
          all_perils_deductible_cad: '',
          all_perils_deductible_other_cad: null,
        });
      } else {
        const willHaveNoCoverage =
          !vehicle.coverages?.comprehensive_enabled &&
          !vehicle.coverages?.all_perils_enabled;
        if (willHaveNoCoverage) {
          triggerPhysicalDamageDecline();
        } else {
          onVehicleChange(index, 'coverages', {
            ...vehicle.coverages,
            collision_enabled: false,
            collision_deductible_cad: '',
            collision_deductible_other_cad: null,
          });
        }
      }
    },
    [index, onVehicleChange, vehicle.coverages, triggerPhysicalDamageDecline],
  );

  const handleComprehensiveToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        onVehicleChange(index, 'coverages', {
          ...vehicle.coverages,
          comprehensive_enabled: true,
          all_perils_enabled: false,
          all_perils_deductible_cad: '',
          all_perils_deductible_other_cad: null,
        });
      } else {
        const willHaveNoCoverage =
          !vehicle.coverages?.collision_enabled &&
          !vehicle.coverages?.all_perils_enabled;
        if (willHaveNoCoverage) {
          triggerPhysicalDamageDecline();
        } else {
          onVehicleChange(index, 'coverages', {
            ...vehicle.coverages,
            comprehensive_enabled: false,
            comprehensive_deductible_cad: '',
            comprehensive_deductible_other_cad: null,
          });
        }
      }
    },
    [index, onVehicleChange, vehicle.coverages, triggerPhysicalDamageDecline],
  );

  const handleAllPerilsToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        onVehicleChange(index, 'coverages', {
          ...vehicle.coverages,
          all_perils_enabled: true,
          collision_enabled: false,
          comprehensive_enabled: false,
          collision_deductible_cad: '',
          collision_deductible_other_cad: null,
          comprehensive_deductible_cad: '',
          comprehensive_deductible_other_cad: null,
        });
      } else {
        triggerPhysicalDamageDecline();
      }
    },
    [index, onVehicleChange, vehicle.coverages, triggerPhysicalDamageDecline],
  );

  const vehicleEntity = vehicle as unknown as Record<string, unknown>;

  const isLiabilityOtherMissing =
    isOtherDeductible(
      vehicle.coverages?.liability_limit_cad || '',
      'liability_limit_cad',
    ) && !vehicle.coverages?.liability_limit_other_cad;
  const isLiabilityInvalid =
    !vehicle.coverages?.liability_limit_cad || isLiabilityOtherMissing;

  const isDcpdOtherMissing =
    isOtherDeductible(
      vehicle.coverages?.dcpd_deductible_cad || '',
      'dcpd_deductible_cad',
    ) && !vehicle.coverages?.dcpd_deductible_other_cad;

  const isCollisionDeductibleRequired =
    vehicle.coverages?.collision_enabled &&
    isFieldRequired(
      'vehicle',
      'coverages.collision_deductible_cad',
      vehicleEntity,
    );
  const isComprehensiveDeductibleRequired =
    vehicle.coverages?.comprehensive_enabled &&
    isFieldRequired(
      'vehicle',
      'coverages.comprehensive_deductible_cad',
      vehicleEntity,
    );
  const isAllPerilsDeductibleRequired =
    vehicle.coverages?.all_perils_enabled &&
    isFieldRequired(
      'vehicle',
      'coverages.all_perils_deductible_cad',
      vehicleEntity,
    );

  const isOpcf20LimitRequired =
    vehicle.coverages?.opcf_20_enabled &&
    isFieldRequired(
      'vehicle',
      'coverages.opcf_20_limit_per_day_cad',
      vehicleEntity,
    );
  const isOpcf27LimitRequired =
    vehicle.coverages?.opcf_27_enabled &&
    isFieldRequired('vehicle', 'coverages.opcf_27_limit_cad', vehicleEntity);
  const isOpcf43PeriodRequired =
    vehicle.coverages?.opcf_43_enabled &&
    isFieldRequired(
      'vehicle',
      'coverages.opcf_43_indemnity_period_months',
      vehicleEntity,
    );

  const {
    isOpen: isAccidentBenefitsOpen,
    onOpen: onAccidentBenefitsOpen,
    onClose: onAccidentBenefitsClose,
  } = useDisclosure();

  const {
    isOpen: isAllCoveragesOpen,
    onOpen: onAllCoveragesOpen,
    onClose: onAllCoveragesClose,
  } = useDisclosure();

  const accidentBenefitsConfirmed =
    vehicle.coverages?.accident_benefits_confirmed || false;
  const allCoveragesConfirmed =
    vehicle.coverages?.all_coverages_confirmed || false;

  const vehicleLabel = [vehicle.vehicle_model_year, vehicle.make, vehicle.model]
    .filter(Boolean)
    .join(' ');

  const handleAccidentBenefitsSave = useCallback(
    (benefits: AccidentBenefits, confirmed: boolean) => {
      const updatedCoverages = {
        ...vehicle.coverages,
        accident_benefits: benefits,
        accident_benefits_confirmed: confirmed,
      };
      onVehicleChange(index, 'coverages', updatedCoverages);
    },
    [index, onVehicleChange, vehicle.coverages],
  );

  const handleAllCoveragesSave = useCallback(
    (coverages: VehicleCoveragesType, confirmed: boolean) => {
      const updatedCoverages = {
        ...coverages,
        all_coverages_confirmed: confirmed,
      };
      onVehicleChange(index, 'coverages', updatedCoverages);
    },
    [index, onVehicleChange],
  );

  return (
    <SectionCard padding={0}>
      <Box
        paddingX={4}
        paddingY={2}
        mb="4"
        bg="gray.200"
        roundedTop="md"
        fontWeight="700"
        color="gray.500"
      >
        Coverages
      </Box>

      {/* Liability Limit */}
      <Flex gap={2} mb={2} paddingX="4" align="center" wrap="wrap">
        <HStack spacing={2}>
          <Select
            variant="main"
            size="sm"
            w="180px"
            minW="180px"
            height={30}
            value={vehicle.coverages?.liability_limit_cad || ''}
            onChange={(e) => handleLiabilityLimitChange(e.target.value)}
            isDisabled={isLoadingCodes}
            borderColor={isLiabilityInvalid ? 'red.600' : undefined}
            _hover={isLiabilityInvalid ? { borderColor: 'red.600' } : undefined}
          >
            {getOptions('liability_limit_cad').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {isOtherDeductible(
            vehicle.coverages?.liability_limit_cad || '',
            'liability_limit_cad',
          ) && (
            <NumberInput
              variant="main"
              size="sm"
              w="100px"
              min={0}
              value={vehicle.coverages?.liability_limit_other_cad ?? ''}
              onChange={(valueString) =>
                onVehicleChange(
                  index,
                  'coverages',
                  valueString ? Number(valueString) : null,
                  'liability_limit_other_cad',
                )
              }
              isDisabled={false}
              isInvalid={isLiabilityOtherMissing}
            >
              <NumberInputField
                placeholder=""
                height={30}
                borderColor={isLiabilityOtherMissing ? 'red.600' : undefined}
              />
            </NumberInput>
          )}
        </HStack>
        <Text fontSize="sm">
          Liability Limit (BI/PD){' '}
          <Text as="span" color="red.600">
            *
          </Text>
        </Text>
      </Flex>

      {/* Direct Compensation */}
      <Flex gap={2} mb={3} paddingX="4" align="center" wrap="wrap">
        <HStack spacing={2}>
          <Select
            variant="main"
            size="sm"
            w="140px"
            minW="140px"
            height={30}
            value={vehicle.coverages?.dcpd_deductible_cad || ''}
            onChange={(e) => handleDcpdChange(e.target.value)}
            isDisabled={isLoadingCodes}
            borderColor={isDcpdOtherMissing ? 'red.600' : undefined}
            _hover={isDcpdOtherMissing ? { borderColor: 'red.600' } : undefined}
          >
            {getOptions('dcpd_deductible_cad').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.value === '' ? 'No DCPD Coverage' : opt.label}
              </option>
            ))}
          </Select>
          {isOtherDeductible(
            vehicle.coverages?.dcpd_deductible_cad || '',
            'dcpd_deductible_cad',
          ) && (
            <NumberInput
              variant="main"
              size="sm"
              w="100px"
              min={0}
              value={vehicle.coverages?.dcpd_deductible_other_cad ?? ''}
              onChange={(valueString) =>
                onVehicleChange(
                  index,
                  'coverages',
                  valueString ? Number(valueString) : null,
                  'dcpd_deductible_other_cad',
                )
              }
              isDisabled={false}
              isInvalid={isDcpdOtherMissing}
            >
              <NumberInputField
                placeholder=""
                height={30}
                borderColor={isDcpdOtherMissing ? 'red.600' : undefined}
              />
            </NumberInput>
          )}
        </HStack>
        <Text fontSize="sm">Direct Compensation</Text>
      </Flex>

      <Divider my={3} />

      {/* Physical Damage Coverage - Checkboxes */}
      <VStack align="start" spacing={2} paddingX="4">
        {/* Collision */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.collision_enabled || false}
            onChange={(e) => handleCollisionToggle(e.target.checked)}
            opacity={vehicle.coverages?.all_perils_enabled ? 0.5 : 1}
          />
          <HStack spacing={2}>
            <Select
              variant="main"
              size="sm"
              w="140px"
              height={30}
              value={vehicle.coverages?.collision_deductible_cad || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                const isOther = isOtherDeductible(
                  newValue,
                  'collision_deductible_cad',
                );
                onVehicleChange(index, 'coverages', {
                  ...vehicle.coverages,
                  collision_deductible_cad: newValue,
                  collision_deductible_other_cad: isOther
                    ? vehicle.coverages?.collision_deductible_other_cad
                    : null,
                });
              }}
              isDisabled={
                isLoadingCodes ||
                !vehicle.coverages?.collision_enabled ||
                vehicle.coverages?.all_perils_enabled
              }
              opacity={
                !vehicle.coverages?.collision_enabled ||
                vehicle.coverages?.all_perils_enabled
                  ? 0.5
                  : 1
              }
              borderColor={
                isCollisionDeductibleRequired &&
                !vehicle.coverages?.collision_deductible_cad
                  ? 'red.600'
                  : undefined
              }
            >
              {getOptions('collision_deductible_cad').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === '' ? 'Select' : opt.label}
                </option>
              ))}
            </Select>
            {isOtherDeductible(
              vehicle.coverages?.collision_deductible_cad || '',
              'collision_deductible_cad',
            ) && (
              <NumberInput
                variant="main"
                size="sm"
                w="90px"
                min={0}
                value={vehicle.coverages?.collision_deductible_other_cad ?? ''}
                onChange={(valueString) =>
                  onVehicleChange(
                    index,
                    'coverages',
                    valueString ? Number(valueString) : null,
                    'collision_deductible_other_cad',
                  )
                }
                isDisabled={
                  !vehicle.coverages?.collision_enabled ||
                  vehicle.coverages?.all_perils_enabled
                }
                opacity={
                  !vehicle.coverages?.collision_enabled ||
                  vehicle.coverages?.all_perils_enabled
                    ? 0.5
                    : 1
                }
              >
                <NumberInputField placeholder="BASE" height={30} />
              </NumberInput>
            )}
          </HStack>
          <Text
            fontSize="sm"
            color={
              !vehicle.coverages?.collision_enabled ||
              vehicle.coverages?.all_perils_enabled
                ? 'gray.400'
                : 'inherit'
            }
          >
            Collision
          </Text>
        </Flex>

        {/* Comprehensive */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.comprehensive_enabled || false}
            onChange={(e) => handleComprehensiveToggle(e.target.checked)}
            opacity={vehicle.coverages?.all_perils_enabled ? 0.5 : 1}
          />
          <HStack spacing={2}>
            <Select
              variant="main"
              size="sm"
              w="140px"
              height={30}
              value={vehicle.coverages?.comprehensive_deductible_cad || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                const isOther = isOtherDeductible(
                  newValue,
                  'comprehensive_deductible_cad',
                );
                onVehicleChange(index, 'coverages', {
                  ...vehicle.coverages,
                  comprehensive_deductible_cad: newValue,
                  comprehensive_deductible_other_cad: isOther
                    ? vehicle.coverages?.comprehensive_deductible_other_cad
                    : null,
                });
              }}
              isDisabled={
                isLoadingCodes ||
                !vehicle.coverages?.comprehensive_enabled ||
                vehicle.coverages?.all_perils_enabled
              }
              opacity={
                !vehicle.coverages?.comprehensive_enabled ||
                vehicle.coverages?.all_perils_enabled
                  ? 0.5
                  : 1
              }
              borderColor={
                isComprehensiveDeductibleRequired &&
                !vehicle.coverages?.comprehensive_deductible_cad
                  ? 'red.600'
                  : undefined
              }
            >
              {getOptions('comprehensive_deductible_cad').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === '' ? 'Select' : opt.label}
                </option>
              ))}
            </Select>
            {isOtherDeductible(
              vehicle.coverages?.comprehensive_deductible_cad || '',
              'comprehensive_deductible_cad',
            ) && (
              <NumberInput
                variant="main"
                size="sm"
                w="90px"
                min={0}
                value={
                  vehicle.coverages?.comprehensive_deductible_other_cad ?? ''
                }
                onChange={(valueString) =>
                  onVehicleChange(
                    index,
                    'coverages',
                    valueString ? Number(valueString) : null,
                    'comprehensive_deductible_other_cad',
                  )
                }
                isDisabled={
                  !vehicle.coverages?.comprehensive_enabled ||
                  vehicle.coverages?.all_perils_enabled
                }
                opacity={
                  !vehicle.coverages?.comprehensive_enabled ||
                  vehicle.coverages?.all_perils_enabled
                    ? 0.5
                    : 1
                }
              >
                <NumberInputField placeholder="" height={30} />
              </NumberInput>
            )}
          </HStack>
          <Text
            fontSize="sm"
            color={
              !vehicle.coverages?.comprehensive_enabled ||
              vehicle.coverages?.all_perils_enabled
                ? 'gray.400'
                : 'inherit'
            }
          >
            Comprehensive
          </Text>
        </Flex>

        {/* All Perils */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.all_perils_enabled || false}
            onChange={(e) => handleAllPerilsToggle(e.target.checked)}
            opacity={
              vehicle.coverages?.collision_enabled ||
              vehicle.coverages?.comprehensive_enabled
                ? 0.5
                : 1
            }
          />
          <HStack spacing={2}>
            <Select
              variant="main"
              size="sm"
              w="140px"
              height={30}
              value={vehicle.coverages?.all_perils_deductible_cad || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                const isOther = isOtherDeductible(
                  newValue,
                  'all_perils_deductible_cad',
                );
                onVehicleChange(index, 'coverages', {
                  ...vehicle.coverages,
                  all_perils_deductible_cad: newValue,
                  all_perils_deductible_other_cad: isOther
                    ? vehicle.coverages?.all_perils_deductible_other_cad
                    : null,
                });
              }}
              isDisabled={
                isLoadingCodes || !vehicle.coverages?.all_perils_enabled
              }
              opacity={!vehicle.coverages?.all_perils_enabled ? 0.5 : 1}
              borderColor={
                isAllPerilsDeductibleRequired &&
                !vehicle.coverages?.all_perils_deductible_cad
                  ? 'red.600'
                  : undefined
              }
            >
              {getOptions('all_perils_deductible_cad').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === '' ? 'Select' : opt.label}
                </option>
              ))}
            </Select>
            {isOtherDeductible(
              vehicle.coverages?.all_perils_deductible_cad || '',
              'all_perils_deductible_cad',
            ) && (
              <NumberInput
                variant="main"
                size="sm"
                w="90px"
                min={0}
                value={vehicle.coverages?.all_perils_deductible_other_cad ?? ''}
                onChange={(valueString) =>
                  onVehicleChange(
                    index,
                    'coverages',
                    valueString ? Number(valueString) : null,
                    'all_perils_deductible_other_cad',
                  )
                }
                isDisabled={!vehicle.coverages?.all_perils_enabled}
                opacity={!vehicle.coverages?.all_perils_enabled ? 0.5 : 1}
              >
                <NumberInputField placeholder="" height={30} />
              </NumberInput>
            )}
          </HStack>
          <Text
            fontSize="sm"
            color={
              !vehicle.coverages?.all_perils_enabled ? 'gray.400' : 'inherit'
            }
          >
            All Perils
          </Text>
        </Flex>
      </VStack>

      <Divider my={3} />

      {/* OPCFs */}
      <VStack align="start" spacing={2} paddingX="4">
        {/* OPCF 20 */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.opcf_20_enabled || false}
            onChange={(e) => handleOpcf20Change(e.target.checked)}
            isDisabled={false}
            size="md"
            colorScheme="blue"
          />
          <Select
            variant="main"
            size="sm"
            w="100px"
            minW="100px"
            height={30}
            value={vehicle.coverages?.opcf_20_limit_per_day_cad ?? ''}
            onChange={(e) =>
              onVehicleChange(
                index,
                'coverages',
                e.target.value ? Number(e.target.value) : null,
                'opcf_20_limit_per_day_cad',
              )
            }
            isDisabled={!vehicle.coverages?.opcf_20_enabled}
            borderColor={
              isOpcf20LimitRequired &&
              !vehicle.coverages?.opcf_20_limit_per_day_cad
                ? 'red.600'
                : undefined
            }
          >
            <option value="">
              {vehicle.coverages?.opcf_20_enabled ? 'Select' : ''}
            </option>
            {OPCF_20_LIMIT_PER_DAY_CAD.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Text fontSize="sm">OPCF 20 Loss Of Use</Text>
        </Flex>

        {/* OPCF 27 */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.opcf_27_enabled || false}
            onChange={(e) => handleOpcf27Change(e.target.checked)}
            isDisabled={false}
            size="md"
            colorScheme="blue"
          />
          <Select
            variant="main"
            size="sm"
            w="130px"
            minW="130px"
            height={30}
            value={vehicle.coverages?.opcf_27_limit_cad ?? ''}
            onChange={(e) =>
              onVehicleChange(
                index,
                'coverages',
                e.target.value ? Number(e.target.value) : null,
                'opcf_27_limit_cad',
              )
            }
            isDisabled={!vehicle.coverages?.opcf_27_enabled}
            borderColor={
              isOpcf27LimitRequired && !vehicle.coverages?.opcf_27_limit_cad
                ? 'red.600'
                : undefined
            }
          >
            <option value="">
              {vehicle.coverages?.opcf_27_enabled ? 'Select' : ''}
            </option>
            {OPCF_27_LIMIT_CAD.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Text fontSize="sm">OPCF 27 Liability</Text>
        </Flex>

        {/* OPCF 43 */}
        <Flex gap={2} w="100%" align="center" wrap="wrap">
          <Checkbox
            isChecked={vehicle.coverages?.opcf_43_enabled || false}
            onChange={(e) => handleOpcf43Change(e.target.checked)}
            isDisabled={false}
            size="md"
            colorScheme="blue"
          />
          <Select
            variant="main"
            size="sm"
            w="140px"
            minW="140px"
            height={30}
            value={vehicle.coverages?.opcf_43_indemnity_period_months ?? ''}
            onChange={(e) =>
              onVehicleChange(
                index,
                'coverages',
                e.target.value ? Number(e.target.value) : null,
                'opcf_43_indemnity_period_months',
              )
            }
            isDisabled={!vehicle.coverages?.opcf_43_enabled}
            borderColor={
              isOpcf43PeriodRequired &&
              !vehicle.coverages?.opcf_43_indemnity_period_months
                ? 'red.600'
                : undefined
            }
          >
            <option value="">
              {vehicle.coverages?.opcf_43_enabled ? 'Select' : ''}
            </option>
            {OPCF_43_INDEMNITY_PERIOD_MONTHS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Text fontSize="sm">OPCF 43 Limited Waiver</Text>
        </Flex>
      </VStack>

      {/* Accident Waiver + Minor Conviction */}
      <Wrap spacing={4} my={3} paddingX="4">
        <WrapItem>
          <Checkbox
            isChecked={vehicle.coverages?.accident_waiver || false}
            onChange={(e) => handleAccidentWaiverChange(e.target.checked)}
            isDisabled={false}
            size="md"
            colorScheme="blue"
          >
            <Text fontSize="sm" whiteSpace="nowrap">
              Accident Waiver
            </Text>
          </Checkbox>
        </WrapItem>
        <WrapItem>
          <Checkbox
            isChecked={vehicle.coverages?.minor_conviction_protection || false}
            onChange={(e) => handleMinorConvictionChange(e.target.checked)}
            isDisabled={false}
            size="md"
            colorScheme="blue"
          >
            <Text fontSize="sm">Minor Conviction Protection</Text>
          </Checkbox>
        </WrapItem>
      </Wrap>

      <Divider my={3} />

      {/* Ride Sharing */}
      <Flex
        gap={4}
        mb={3}
        paddingX="4"
        align="flex-start"
        direction={{ base: 'column', sm: 'row' }}
      >
        <Checkbox
          isChecked={vehicle.coverages?.ride_sharing_enabled || false}
          onChange={(e) => {
            const updatedCoverages = applyRideSharingDefaults(
              (vehicle.coverages || {}) as VehicleCoveragesType,
              provinceCode,
              e.target.checked,
            );
            onVehicleChange(index, 'coverages', updatedCoverages);
          }}
          isDisabled={false}
          size="md"
          colorScheme="blue"
          mt={{ base: 0, sm: 2 }}
        >
          <Text fontSize="sm" whiteSpace="nowrap">
            Ride-Sharing
          </Text>
        </Checkbox>
        <Box flex="1" maxW={{ base: '100%', sm: '250px' }} w="100%">
          <MultiSelect
            selectedIds={(vehicle.coverages?.ride_sharing_companies || [])
              .map(rideSharingValueToId)
              .filter((id) => id !== -1)}
            availableItems={RIDE_SHARING_ITEMS}
            isLoading={false}
            onChange={(selectedIds) => {
              const values = selectedIds
                .map(rideSharingIdToValue)
                .filter(Boolean);
              onVehicleChange(
                index,
                'coverages',
                values,
                'ride_sharing_companies',
              );
            }}
            label="Select companies"
            itemLabelSingular="company"
            itemLabelPlural="companies"
            isDisabled={!vehicle.coverages?.ride_sharing_enabled}
            showTags={false}
          />
        </Box>
      </Flex>

      {/* Action Buttons - Full width, solid */}
      <VStack w="100%" spacing={0}>
        <Button
          w="100%"
          size="lg"
          bg={accidentBenefitsConfirmed ? 'blue.700' : 'red.700'}
          color="white"
          _hover={{ bg: accidentBenefitsConfirmed ? 'blue.800' : 'red.800' }}
          leftIcon={accidentBenefitsConfirmed ? <CheckIcon /> : <WarningIcon />}
          isDisabled={false}
          roundedTop="0"
          roundedBottom="0"
          onClick={onAccidentBenefitsOpen}
        >
          Accident Benefits
        </Button>
        <Button
          w="100%"
          size="lg"
          bg={allCoveragesConfirmed ? 'blue.700' : 'red.700'}
          color="white"
          _hover={{ bg: allCoveragesConfirmed ? 'blue.800' : 'red.800' }}
          leftIcon={allCoveragesConfirmed ? <CheckIcon /> : <WarningIcon />}
          isDisabled={false}
          roundedBottom="md"
          roundedTop="0"
          onClick={onAllCoveragesOpen}
        >
          All Coverages
        </Button>
      </VStack>

      <AccidentBenefitsModal
        isOpen={isAccidentBenefitsOpen}
        onClose={onAccidentBenefitsClose}
        onSave={handleAccidentBenefitsSave}
        initialBenefits={vehicle.coverages?.accident_benefits}
        initialConfirmed={vehicle.coverages?.accident_benefits_confirmed}
        vehicleLabel={vehicleLabel}
        quoteId={quoteId}
        vehicleVin={vehicle.vin || ''}
        vehicleId={vehicle.id}
      />

      <AllCoveragesModal
        isOpen={isAllCoveragesOpen}
        onClose={onAllCoveragesClose}
        onSave={handleAllCoveragesSave}
        initialCoverages={vehicle.coverages as VehicleCoveragesType}
        vehicleLabel={vehicleLabel}
        quoteId={quoteId}
        vehicleVin={vehicle.vin || ''}
        vehicleId={vehicle.id}
        provinceCode={provinceCode}
      />

      <LiabilityLimitConfirmationModal {...liabilityModalProps} />
      <DcpdDeclineConfirmationModal {...dcpdModalProps} />
      <PhysicalDamageDeclineConfirmationModal {...physicalDamageModalProps} />
      <CoverageConsentModal {...opcf20ModalProps} />
      <CoverageConsentModal {...opcf27ModalProps} />
      <CoverageConsentModal {...accidentWaiverModalProps} />
      <CoverageConsentModal {...minorConvictionModalProps} />
      <CoverageConsentModal {...opcf43ModalProps} />
    </SectionCard>
  );
};

export default VehicleCoverages;
